import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnDestroy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { DataroomActionsService } from '@shared/dataroom/dataroom-actions.service';
import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs';
import { NavigableNode } from '@shared/dataroom/dataroom.component';
import { catchError, delay, filter, finalize, map, take, tap } from 'rxjs/operators';
import { FileInputValue } from '@ngx-dropzone/cdk';
import { FormControl } from '@angular/forms'
import { TypedFormGroup } from '@feature/forms/form-model';;
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import {
  MediaType,
  PdfMediaTypes,
  WordDocumentMediaTypes,
  ImageMediaTypes,
  VideoMediaTypes,
  AudioMediaTypes,
  SpreadsheetMediaTypes,
  PresentationMediaTypes,
  ArchiveMediaTypes,
  CsvMediaTypes,
  JsonMediaTypes,
} from '@core/attachment/media-type.typings';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { getIcon, getIconColor } from '@core/attachment/helpers';
import { TranslateService } from '@ngx-translate/core';
import { FileSizePipe } from '@shared/pipes/file-size/file-size.pipe';
import { DATAROOM_MAX_FILE_SIZE } from '@shared/file';
import { DataroomNode } from '@core/models/dataroom-node.model';
import { validateFileMediaType } from '@feature/forms/wai-validators';
import { SnackBarService } from '@feature/snack-bar/snack-bar.service';

export interface UploadDialogData {
  activeScope: BehaviorSubject<NavigableNode>;
  onUploadComplete?(response?: any): void;
  isUpdate?: boolean;
  node?: DataroomNode;
}

interface FileItem {
  name: string;
  type: string;
  size: number;
  file: File;
  isUploading: boolean;
  uploaded: boolean;
  error: string;
}

interface UploadResult {
  success: boolean;
  endOfFiles: boolean;
}

@UntilDestroy()
@Component({
    selector: 'wai-upload-dialog',
    templateUrl: './upload-dialog.component.html',
    styleUrls: ['./upload-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class UploadDialogComponent implements OnDestroy {
  public asTypedFormGroup = (x: unknown): TypedFormGroup<any> => x as TypedFormGroup<any>;
  public asFileItemArray = (x: unknown): FileItem[] => x as FileItem[];

  isUploading = false;
  validators = [];
  filesControl = new FormControl<FileInputValue>(null, this.validators);
  files: BehaviorSubject<FileItem[]> = new BehaviorSubject([]);
  displayedColumns: string[] = ['name', 'size', 'actions'];
  tableDataSource: Observable<FileItem[]> = this.files;
  totalSize: Observable<number> = this.files.pipe(
    map((files: FileItem[]) => files.reduce((acc, file) => acc + file.size, 0)),
  );
  hasFilesToUpload: Observable<boolean> = this.files.pipe(
    map((files: FileItem[]) => files.some((file) => !file.uploaded && !file.error)),
  );
  allFilesUploadedSuccessfully$: Observable<boolean> = this.files.pipe(
    map((files: FileItem[]) =>
      files.length > 0 &&
      files.every(file => file.uploaded === true && !file.error)
    ),
  );
  showSuccessMessage$: Observable<boolean> = combineLatest([
    this.allFilesUploadedSuccessfully$,
    this.files
  ]).pipe(
    map(([allSuccess, files]) => !this.isUploading && allSuccess && files.length > 0)
  );
  maxFileSize = DATAROOM_MAX_FILE_SIZE;

  private readonly pdfOnlyExtensions = ['pdf'];
  private readonly specialExtensions = {
    numbers: 'application/vnd.apple.numbers',
  };
  private readonly allowedMediaTypes = [
    ...PdfMediaTypes,
    ...WordDocumentMediaTypes,
    ...ImageMediaTypes,
    ...VideoMediaTypes,
    ...AudioMediaTypes,
    ...SpreadsheetMediaTypes,
    ...PresentationMediaTypes,
    ...ArchiveMediaTypes,
    ...CsvMediaTypes,
    ...JsonMediaTypes,
  ];
    
  private readonly MAX_FILES = 10;

  constructor(
    @Inject(MAT_DIALOG_DATA) public dialogData: UploadDialogData,
    private dialogRef: MatDialogRef<UploadDialogComponent>,
    private actionsService: DataroomActionsService,
    private cdr: ChangeDetectorRef,
    private translate: TranslateService,
    private snackBarService: SnackBarService,
  ) {
    this.filesControl.valueChanges.pipe(untilDestroyed(this)).subscribe(this.addFiles.bind(this));
  }

  ngOnDestroy(): void {
    // Cleanup handled by @UntilDestroy decorator
  }

  public clickOnUpload(): void {
    this.clearErrors();
    this.isUploading = true;
    this.uploadAll();
  }

  public uploadAll() {
    this.upload().subscribe((result: UploadResult) => {
      if (!result.endOfFiles) {
        this.uploadAll();
      } else {
        this.dialogData?.onUploadComplete?.();
        this.isUploading = false;
        this.files.next(this.files.value);
        this.autoCloseOnSuccess();
      }
    });
  }

  public upload(): Observable<UploadResult> {
    const fileItem: FileItem = this.files.value.find((file) => !file.uploaded && !file.isUploading && !file.error);

    if (!fileItem)
      return of({
        success: false,
        endOfFiles: true,
      });

    fileItem.isUploading = true;

    if (this.dialogData.isUpdate && this.dialogData.node) {
      return this.actionsService
        .updateFile(fileItem.file, this.dialogData.node, this.dialogData?.activeScope?.value?.id)
        .pipe(
          tap(() => {
            fileItem.uploaded = true;
            if (this.dialogData.isUpdate) {
              this.dialogRef.close();
            }
          }),
          map(() => ({
            success: true,
            endOfFiles: false,
          })),
          catchError((error) => {
            const errorMsg = error?.error?.nonFieldErrors;
            if (errorMsg) {
              fileItem.error =
                errorMsg?.join?.(', ') || errorMsg || this.translate.instant('INTL_ERROR_UPLOADING_FILE');
            }
            return of({
              success: false,
              endOfFiles: false,
            });
          }),
          finalize(() => {
            fileItem.isUploading = false;
            this.cdr.detectChanges();
          }),
        );
    }

    return this.actionsService.upload(fileItem.file, this.dialogData?.activeScope?.value?.id).pipe(
      tap((response) => {
        fileItem.uploaded = true;
        if (this.dialogData?.onUploadComplete) {
          this.dialogData.onUploadComplete(response);
        }
      }),
      map(() => ({
        success: true,
        endOfFiles: false,
      })),
      catchError((error) => {
        const raw = error?.error as string;
        const errorMsg = error?.error?.nonFieldErrors;
        if (typeof raw === 'string' && raw.includes('value too long for type character varying(150)')) {
          this.snackBarService.error('INTL_DATAROOM_FILENAME_TOO_LONG');
        } else if (errorMsg) {
          fileItem.error = errorMsg?.join?.(', ') || errorMsg || this.translate.instant('INTL_ERROR_UPLOADING_FILE');
          this.snackBarService.error('INTL_ERROR_UPLOADING_FILE');
        } else {
          fileItem.error = this.translate.instant('INTL_ERROR_UPLOADING_FILE');
          this.snackBarService.error('INTL_ERROR_UPLOADING_FILE');
        }
        return of({
          success: false,
          endOfFiles: false,
        });
      }),
      finalize(() => {
        fileItem.isUploading = false;
        this.cdr.detectChanges();
      }),
    );
  }

  public remove(index: number): void {
    this.removeFile(index);
  }

  public removeAll(): void {
    this.files.next([]);
  }

  public getIcon(type: MediaType): IconDefinition {
    return getIcon(type);
  }

  public getIconColor(type: MediaType): string {
    return getIconColor(type);
  }

  public close(): void {
    this.dialogRef.close();
  }

  private addFiles(files: File[]): void {

    
  const currentCount = this.files.value.length;
  const remainingSlots = this.MAX_FILES - currentCount;

  // 🚫 If already at limit
  if (remainingSlots <= 0) {
    this.snackBarService.open(
      'You can upload a maximum of 10 files.',
      'info'
    );
    return;
  }

    const invalidFiles = files.filter((file) => !this.isValidFile(file));
    const validFiles = files.filter((file) => this.isValidFile(file));

    if (invalidFiles.length) {
      this.filesControl.setErrors({
        invalidFileExtensions: invalidFiles.map((file) => file.name).join(', '),
      });
      // return;
    }

    const fileDuplicates = validFiles.filter((file) => this.files.value.some((f) => f.name === file.name));

    if (fileDuplicates.length) {
      this.filesControl.setErrors({ fileDuplicates });
      return;
    }
// 🚫 user selected more than allowed in one go
if (currentCount === 0 && validFiles.length > this.MAX_FILES) {
  this.snackBarService.open(
    'You can upload a maximum of 10 files.',
    'info'
  );
  return;
}

// 🚫 user exceeds remaining slots
if (validFiles.length > remainingSlots) {
  this.snackBarService.open(
    `You can select only ${remainingSlots} more file${remainingSlots === 1 ? '' : 's'} (max 10).`,
    'info'
  );
  return;
}

  const fileItems = validFiles.map((file) => ({
    name: file.name,
    type: file.type,
    size: file.size,
    file,
    isUploading: false,
    uploaded: false,
    error: this.validateFileSize(file),
  }));

  this.files.next([...this.files.value, ...fileItems]);
  }

  private isValidFile(file: File): boolean {
    const targetFileIsPdf = this.dialogData.node?.name?.toLowerCase().endsWith('.pdf');
    const extension = file.name.split('.').pop()?.toLowerCase();

    // Handle PDF update case
    if (targetFileIsPdf) {
      return this.pdfOnlyExtensions.includes(extension || '');
    }

    // Handle special file types by extension
    if (extension && this.specialExtensions[extension]) {
      return true;
    }

    // Handle regular media types
    return validateFileMediaType(file, this.allowedMediaTypes);
  }

  private removeFile(index: number): void {
    const files = [...this.files.value];
    files.splice(index, 1);
    this.files.next(files);
  }

  private clearErrors(): void {
    this.filesControl.setErrors(null);
  }

  private validateFileSize(file: File): string | null {
    if (file.size > this.maxFileSize) {
      return this.translate.instant('INTL_DATAROOM_MAX_FILE_SIZE_VALIDATION_MESSAGE', {
        maxFileSize: new FileSizePipe(this.translate.currentLang).transform(this.maxFileSize),
      });
    }

    return null;
  }

  private autoCloseOnSuccess(): void {
    this.allFilesUploadedSuccessfully$
      .pipe(
        filter(allSuccess => allSuccess === true),
        take(1),
        delay(4500),
        tap(() => this.dialogRef.close())
      )
      .subscribe();
  }
}
